import xor from 'lodash/xor';
import { ReactNode, useCallback, useRef, useState } from 'react';

const EMPTY_VALUES: string[] = [];

export type SelectedOption = {
  value: string;
  label: ReactNode;
};

type UseMultiSelectParams = {
  value?: string[];
  onChange?: (values: string[]) => void;
  disabled?: boolean;
};

export const useMultiSelect = ({
  value,
  onChange,
  disabled,
}: UseMultiSelectParams) => {
  const optionsRegistry = useRef(new Map<string, ReactNode>());
  const [, setVersion] = useState(0);

  const selectedValues = value ?? EMPTY_VALUES;

  const selectedOptions = selectedValues.flatMap<SelectedOption>(
    selectedValue => {
      const label = optionsRegistry.current.get(selectedValue);
      return label === undefined ? [] : [{ value: selectedValue, label }];
    },
  );

  const registerOption = useCallback(
    (optionValue: string, label: ReactNode) => {
      optionsRegistry.current.set(optionValue, label);
      setVersion(version => version + 1);
    },
    [],
  );

  const unregisterOption = useCallback((optionValue: string) => {
    optionsRegistry.current.delete(optionValue);
    setVersion(version => version + 1);
  }, []);

  const handleToggle = useCallback(
    (optionValue: string) => {
      if (disabled) return;
      onChange?.(xor(selectedValues, [optionValue]));
    },
    [disabled, onChange, selectedValues],
  );

  return {
    selectedValues,
    selectedOptions,
    handleToggle,
    registerOption,
    unregisterOption,
  };
};
